import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vModelDynamic as _vModelDynamic, withDirectives as _withDirectives, unref as _unref, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id"]
const _hoisted_2 = ["for"]
const _hoisted_3 = {
  key: 0,
  class: "flex items-center w-auto px-2 mr-2 text-sm leading-tight border-black rounded appearance-none bg-zinc-100 text-sage-500 h-9"
}
const _hoisted_4 = ["data-cy", "id", "maxlength", "readonly", "type"]
const _hoisted_5 = ["data-cy", "id", "maxlength", "placeholder", "type", "autocomplete"]
const _hoisted_6 = {
  key: 2,
  class: "px-2 mt-2 text-xs leading-tight text-red-500"
}
const _hoisted_7 = {
  key: 3,
  class: "px-2 mt-2 text-xs leading-tight text-sage-500"
}

import { computed, onMounted, PropType } from 'vue';
import { ref } from 'vue';
import Icon from './icon/Icon';
import IconComponent from './icon/IconComponent.vue';

type TextInputType = string | number | null;


export default /*#__PURE__*/_defineComponent({
  __name: 'TextInputComponent',
  props: {
  dataTestId: {
    type: String,
    default: 'textInput'
  },
  id: { type: String, required: false },
  value: {
    type: [String, Number],
    required: false
  },
  label: {
    type: String,
    default: ''
  },
  helperText: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: 'Enter a value'
  },
  type: {
    type: String,
    default: 'text'
  },
  regex: {
    type: RegExp,
    default: new RegExp(/^(?=.{1,75}$).*/)
  },
  required: {
    type: Boolean,
    default: false
  },
  errorText: {
    type: String,
    default: 'Please enter a value'
  },
  maxLength: {
    type: String,
    default: '55'
  },
  readonly: {
    type: Boolean,
    default: false
  },
  textIcon: {
    type: Number as PropType<Icon>,
    default: Icon.NONE
  },
  hideValue: {
    type: Boolean,
    default: false
  }
},
  emits: ['input', 'change', 'blur', 'validationState'],
  setup(__props, { emit }) {

const props = __props


const hover = ref(false);
const clicked = ref(false);



const textInputValue = ref<string | number | null>(null);
const isPristine = ref(true);

onMounted(() => {
  updateInputValue();
  emitValidationState();
});

function getCleanValue(newValue: TextInputType) {
  let value: TextInputType = newValue;
  if (props.type === 'text' && typeof newValue === 'string' && textInputValue) {
    value = newValue.toString().replace(/^\s+/g, ''); // Replace leading whitespace only
  } else if (props.type === 'number' && typeof newValue === 'string') {
    value = parseFloat(newValue);
  }
  if (!value && value !== 0) {
    value = null;
  }
  return value;
}

const showError = ref(() => {
  return !isPristine.value && !isValid.value;
});

const hoverAndNotClicked = ref(() => {
  return hover.value && !clicked.value;
});

const isValid = computed(() => {
  const regexTest = props.regex?.test(String(textInputValue.value));
  if (props.required) {
    return !!(textInputValue.value || textInputValue.value === 0) && regexTest;
  }
  return regexTest;
});

const handleEventValue = (event: Event) => {
  const inputValue = (event.target as HTMLInputElement).value;
  return inputValue;
};

function handleInputEvent(event: Event): void {
  const eventValue = handleEventValue(event);
  const value = getCleanValue(eventValue);
  textInputValue.value = value;
  emitValidationState();
  emit('input', value);
}

function handleChangeEvent(event: Event): void {
  const eventValue = handleEventValue(event);
  const value = getCleanValue(eventValue);
  textInputValue.value = value;
  emitValidationState();
  emit('change', value);
}

function onBlur(): void {
  isPristine.value = false;
  emit('blur');
}

function emitValidationState(): void {
  emit('validationState', {
    isValid: isValid,
    errorMessage: props.errorText
  });
}

function updateInputValue(): void {
  if (props.value || props.value === 0) {
    textInputValue.value = props.value;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", { id: __props.id }, [
    _createElementVNode("div", {
      class: _normalizeClass(["px-2 text-xs field-labels", {
        'text-red-500': showError.value(),
        'text-sage-500': !showError.value(),
        'mt-4': !__props.label
      }]),
      for: __props.id
    }, _toDisplayString(__props.label), 11, _hoisted_2),
    (__props.readonly)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (__props.textIcon)
            ? (_openBlock(), _createBlock(IconComponent, {
                key: 0,
                class: "mr-2",
                icon: __props.textIcon
              }, null, 8, ["icon"]))
            : _createCommentVNode("", true),
          _withDirectives(_createElementVNode("input", {
            "data-cy": __props.dataTestId,
            class: "w-full border-none outline-none bg-zinc-100 text-sage-500",
            id: __props.id,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((textInputValue).value = $event)),
            maxlength: __props.maxLength,
            placeholder: "No value present",
            readonly: __props.readonly,
            type: __props.hideValue ? 'password' : 'text'
          }, null, 8, _hoisted_4), [
            [_vModelDynamic, textInputValue.value]
          ]),
          _createVNode(IconComponent, {
            icon: _unref(Icon).LOCK
          }, null, 8, ["icon"])
        ]))
      : (_openBlock(), _createElementBlock("div", {
          key: 1,
          class: _normalizeClass(["flex items-center w-auto px-2 mr-2 text-sm leading-tight text-white border-black appearance-none h-9", {
        'border-red-500 border-b-2': showError.value(),
        'border-b': !showError.value() && !hoverAndNotClicked.value(),
        'border-b-2': !showError.value() && hoverAndNotClicked.value()
      }]),
          onMouseover: _cache[4] || (_cache[4] = ($event: any) => (hover.value = true)),
          onMouseleave: _cache[5] || (_cache[5] = ($event: any) => {
        hover.value = false;
        clicked.value = false;
      }),
          onMousedown: _cache[6] || (_cache[6] = ($event: any) => (clicked.value = true))
        }, [
          (__props.textIcon)
            ? (_openBlock(), _createBlock(IconComponent, {
                key: 0,
                icon: __props.textIcon,
                class: _normalizeClass(["mr-2", {
          '-mb-px': !showError.value() && hoverAndNotClicked.value(),
          'mt-px': showError.value()
        }])
              }, null, 8, ["icon", "class"]))
            : _createCommentVNode("", true),
          _withDirectives(_createElementVNode("input", {
            "data-cy": __props.dataTestId,
            class: _normalizeClass(["w-full border-none outline-none text-sage-500", {
          'border-red-500': showError.value(),
          '-mb-px': !showError.value() && hoverAndNotClicked.value()
        }]),
            id: __props.id,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((textInputValue).value = $event)),
            maxlength: __props.maxLength,
            placeholder: __props.placeholder,
            onBlur: onBlur,
            onInput: _cache[2] || (_cache[2] = ($event: any) => (handleInputEvent($event))),
            onChange: _cache[3] || (_cache[3] = ($event: any) => (handleChangeEvent($event))),
            type: __props.hideValue ? 'password' : 'text',
            autocomplete: __props.hideValue ? 'current-password' : 'off'
          }, null, 42, _hoisted_5), [
            [_vModelDynamic, textInputValue.value]
          ]),
          (__props.required)
            ? (_openBlock(), _createBlock(IconComponent, {
                key: 1,
                icon: !showError.value() && !isPristine.value ? _unref(Icon).GREEN_CHECK : _unref(Icon).CAUTION,
                class: _normalizeClass({
          '-mb-px': !showError.value() && hoverAndNotClicked.value(),
          'mt-px': showError.value()
        })
              }, null, 8, ["icon", "class"]))
            : _createCommentVNode("", true)
        ], 34)),
    (showError.value())
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(__props.errorText), 1))
      : (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(__props.helperText), 1))
  ], 8, _hoisted_1))
}
}

})